import React, {useEffect, useMemo, useRef, useState} from 'react'
import {Box, HStack, IconButton, Flex, useSlotRecipe} from '@chakra-ui/react3'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {sliderSlotRecipe} from './index.recipe'
import {ArrowLeftIcon, ArrowRightIcon} from '../../icons'
import {clamp} from 'lodash'

function Slider({children, itemsPerSlide = 1, variant = 'default', gap = 8}) {
    const {formatMessage} = useIntl()
    const recipe = useSlotRecipe({recipe: sliderSlotRecipe})
    const styles = useMemo(() => recipe({variant}), [recipe, variant])

    const containerRef = useRef(null)
    const firstSlideRef = useRef(null)
    const startX = useRef(0)
    const startScrollLeft = useRef(0)
    const [isDragging, setIsDragging] = useState(false)

    const items = React.Children.map(children, (child) => <>{child}</>)
    const count = items.length

    const indicatorNumber = Math.ceil(count - itemsPerSlide + 1)
    const maxIndex = Math.max(0, count - itemsPerSlide)
    const [current, setCurrent] = useState(0)
    const canPrev = current > 0
    const canNext = current < maxIndex

    const getStep = () => {
        const el = containerRef.current
        if (!el) return 0
        const slideW = firstSlideRef.current?.offsetWidth ?? 0
        return slideW + gap * 4
    }

    const prev = () => {
        const el = containerRef.current
        if (!el) return
        const step = getStep()
        el.scrollTo({left: el.scrollLeft - step, behavior: 'smooth'})
    }
    const next = () => {
        const el = containerRef.current
        if (!el) return
        const step = getStep()
        el.scrollTo({left: el.scrollLeft + step, behavior: 'smooth'})
    }
    const prevNextButtons = (
        <Flex gap={2}>
            <IconButton
                aria-label={formatMessage({
                    id: 'global.gallery.scroll_prev',
                    defaultMessage: 'Scroll previous'
                })}
                onClick={prev}
                size="md"
                disabled={!canPrev}
            >
                <ArrowLeftIcon sx={{color: 'icons.primary'}} />
            </IconButton>
            <IconButton
                aria-label={formatMessage({
                    id: 'global.gallery.scroll_next',
                    defaultMessage: 'Scroll next'
                })}
                onClick={next}
                size="md"
                disabled={!canNext}
            >
                <ArrowRightIcon sx={{color: 'icons.primary'}} />
            </IconButton>
        </Flex>
    )

    const onPointerDown = (e) => {
        const el = containerRef.current
        if (!el) return
        startX.current = e.clientX
        startScrollLeft.current = el.scrollLeft
        setIsDragging(true)
    }

    const SWIPE_THRESHOLD = 50
    const onPointerMove = (e) => {
        if (!isDragging) return
        const el = containerRef.current
        if (!el) return
        const dx = e.clientX - startX.current
        if (Math.abs(dx) < SWIPE_THRESHOLD) return
        el.scrollLeft = startScrollLeft.current - dx
    }

    const onPointerUp = () => {
        const el = containerRef.current
        if (!el) return
        setIsDragging(false)

        const step = getStep()
        const maxIdx = Math.max(0, Math.round((el.scrollWidth - el.clientWidth) / step))
        const idx = clamp(Math.round(el.scrollLeft / step), 0, maxIdx)
        el.scrollTo({left: idx * step, behavior: 'smooth'})
    }

    useEffect(() => {
        const el = containerRef.current
        if (!el) return

        const onScroll = () => {
            const slide = firstSlideRef.current
            if (!slide) return
            const step = slide.offsetWidth + gap
            if (step <= 0) return
            const i = Math.round(el.scrollLeft / step)
            setCurrent(Math.max(0, Math.min(i, Math.max(0, count - itemsPerSlide))))
        }
        el.addEventListener('scroll', onScroll, {passive: true})
        return () => el.removeEventListener('scroll', onScroll)
    }, [count, itemsPerSlide, gap])

    return (
        <Flex
            role="region"
            aria-roledescription="carousel"
            aria-label="Content slider"
            {...styles.sliderContainer}
        >
            {/* Controls */}
            <Flex {...styles.controlsContainer}>
                {variant === 'gallery' ? (
                    <>
                        {prevNextButtons}
                        <HStack {...styles.pagination}>
                            {Array.from({length: indicatorNumber}).map((_, i) => (
                                <Box
                                    className="inespagination"
                                    key={i}
                                    h="3px"
                                    w={i === current ? '36px' : '16px'}
                                    bg={
                                        i === current
                                            ? 'border.brand'
                                            : 'background.actions.carousel-ticker'
                                    }
                                    borderRadius="full"
                                    transition="all 0.3s ease"
                                />
                            ))}
                        </HStack>
                    </>
                ) : (
                    <Flex {...styles.videoControlContainer}>{prevNextButtons}</Flex>
                )}
            </Flex>

            <Flex
                ref={containerRef}
                className="slides"
                scrollSnapType={isDragging ? 'none' : 'x mandatory'}
                scrollBehavior={isDragging ? 'auto' : 'smooth'}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                gap={gap}
                {...styles.scrollContainer}
            >
                {items.map((child, idx) => {
                    const isVisible = idx >= current && idx < current + itemsPerSlide
                    return (
                        <Box
                            key={idx}
                            ref={idx === 0 ? firstSlideRef : null}
                            data-testid="slide"
                            width={`calc((100% - (${itemsPerSlide - 1} * ${
                                gap * 4
                            }px)) / ${itemsPerSlide})`}
                            scrollSnapAlign="start"
                            role="group"
                            aria-roledescription="slide"
                            inert={!isVisible ? '' : undefined}
                            aria-hidden={!isVisible || undefined}
                            tabIndex={isVisible ? 0 : -1}
                            {...styles.slide}
                        >
                            {child}
                        </Box>
                    )
                })}
            </Flex>
        </Flex>
    )
}

Slider.propTypes = {
    children: PropTypes.node.isRequired,
    itemsPerSlide: PropTypes.number,
    variant: PropTypes.string,
    onIndexChange: PropTypes.func,
    gap: PropTypes.number
}

export default Slider
